<template>
  <div class="flex min-h-full flex-1 flex-col px-8 py-8 bg-white text-gray-800 w-full items-center">
    <div class="flex gap-4 sm:max-w-md flex-col">
      <div class="flex-shrink-0 self-center">
        <i class="text-4xl icon-audio-mic-solid text-green-600" />
      </div>
      <div>
        <h2 class="text-2xl font-bold leading-9 tracking-tight text-gray-900">
          <template v-if="videoPermissionReq">Camera and </template>Microphone Access Required
        </h2>
        <p class="mt-1">
          LiveSwitch Concierge requires access to your
          <template v-if="videoPermissionReq">camera and </template>microphone. Please
          <span class="text-green-600">
            allow <span v-if="videoPermissionReq">camera and </span>microphone access
          </span>
          from site settings to continue.
        </p>
        <p class="mt-2" v-if="mediaError">
          <div>
            <h3>How to grant microphone permissions.</h3>
          </div>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-title class="text-green-600 text-lg">In Browser</v-expansion-panel-title>
              <v-expansion-panel-text :eager="true">
                <div v-for="step in inBrowserArrayMic" :key="step.title">
                <h3>{{ step.title }}</h3>
                <p>{{ step.instructions }}</p>
                <img v-if="step.image" :src="step.image" />
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title class="text-green-600 text-lg">System Settings</v-expansion-panel-title>
            <v-expansion-panel-text :eager="true">
              <div v-for="step in inSystemArrayMic" :key="step.title">
                <h3>{{ step.title }}</h3>
                <p>{{ step.instructions }}</p>
                <img v-if="step.image" :src="step.image" />
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
          </v-expansion-panels>
          <div>
						<h3>How to grant camera permissions.</h3>
					</div>
					<v-expansion-panels>
						<v-expansion-panel>
							<v-expansion-panel-title class="text-green-600 text-lg">In Browser</v-expansion-panel-title>
							<v-expansion-panel-text :eager="true">
								<div v-for="step in inBrowserArrayCam" :key="step.title">
									<h3>{{ step.title }}</h3>
									<p>{{ step.instructions }}</p>
									<img v-if="step.image" :src="step.image" />
								</div>
							</v-expansion-panel-text>
						</v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-title class="text-green-600 text-lg">System Settings</v-expansion-panel-title>
              <v-expansion-panel-text :eager="true">
                <div v-for="step in inSystemArrayCam" :key="step.title">
                  <h3>{{ step.title }}</h3>
									<p>{{ step.instructions }}</p>
									<img v-if="step.image" :src="step.image" />
								</div>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
        </p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { captureException } from '@/bubble'
import { SourceType } from '@/utility/Constants'
import DeviceUtility from '@/utility/DeviceUtility'
import { type Ref, ref, onMounted, onErrorCaptured } from 'vue'
import { useMeetingConfiguration } from '@meeting/composables/useMeetingConfiguration';

const props = defineProps<{
  sourceType: SourceType
}>()
const emits = defineEmits<{
  (e: 'transition'): void
}>()
const mediaError: Ref<string> = ref('')
const videoPermissionReq = ref(false)

let inBrowserArrayMic = ref<{ title?: string; image?: string; instructions: string }[]>([])
let inBrowserArrayCam = ref<{ title?: string; image?: string; instructions: string }[]>([])
let inSystemArrayMic = ref<{ title?: string; image?: string; instructions: string }[]>([])
let inSystemArrayCam = ref<{ title?: string; image?: string; instructions: string }[]>([])

// Permission steps data organized by device/browser/permission type
import { permissionStepsData } from '../utility/permissions/PermissionStepsData'

const getImages = () => {
  // Set appropriate instructions based on device and browser
  if (DeviceUtility.isMac) {
    if (DeviceUtility.isChrome) {
      inBrowserArrayMic.value = permissionStepsData.DesktopChromeMicrophoneSteps
      inBrowserArrayCam.value = permissionStepsData.DesktopChromeCameraSteps
      inSystemArrayMic.value = permissionStepsData.macOSSystemChromeMicrophoneSteps
      inSystemArrayCam.value = permissionStepsData.macOSSystemChromeCameraSteps
    } else if (DeviceUtility.isSafari) {
      inBrowserArrayMic.value = permissionStepsData.MacSafariMicrophoneSettings
      inBrowserArrayCam.value = permissionStepsData.MacSafariCameraSettings
      inSystemArrayMic.value = permissionStepsData.macOSSystemMicrophoneSteps
      inSystemArrayCam.value = permissionStepsData.macOSSystemCameraSteps
    } else if (DeviceUtility.isFirefox) {
      inBrowserArrayMic.value = permissionStepsData.DesktopFirefoxMicrophoneSettings
      inBrowserArrayCam.value = permissionStepsData.DesktopFirefoxCameraSettings
      inSystemArrayMic.value = permissionStepsData.macOSSystemFirefoxMicrophoneSteps
      inSystemArrayCam.value = permissionStepsData.macOSSystemFirefoxCameraSteps
    }
  } else if (DeviceUtility.isWindows) {
    if (DeviceUtility.isChrome) {
      inBrowserArrayMic.value = permissionStepsData.DesktopChromeMicrophoneSteps
      inBrowserArrayCam.value = permissionStepsData.DesktopChromeCameraSteps
      inSystemArrayMic.value = permissionStepsData.windowsSystemMicrophoneSettings
      inSystemArrayCam.value = permissionStepsData.windowsSystemCameraSettings
    } else if (DeviceUtility.isFirefox) {
      inBrowserArrayMic.value = permissionStepsData.DesktopFirefoxMicrophoneSettings
      inBrowserArrayCam.value = permissionStepsData.DesktopFirefoxCameraSettings
      inSystemArrayMic.value = permissionStepsData.windowsSystemMicrophoneSettings
      inSystemArrayCam.value = permissionStepsData.windowsSystemCameraSettings
    }
  } else if (DeviceUtility.isAndroid) {
    if (DeviceUtility.isChrome) {
      inBrowserArrayMic.value = permissionStepsData.AndroidChromeMicrophoneSteps
      inSystemArrayMic.value = permissionStepsData.AndroidSystemMicrophoneSteps
      inBrowserArrayCam.value = permissionStepsData.AndroidChromeCameraSteps
      inSystemArrayCam.value = permissionStepsData.AndroidSystemCameraSteps
    }
  } else if (DeviceUtility.isIOS) {
    if (DeviceUtility.isChrome) {
      inBrowserArrayMic.value = permissionStepsData.IOSChromeMicrophoneSettings
      inSystemArrayMic.value = permissionStepsData.IosSystemMicrophoneSteps
      inBrowserArrayCam.value = permissionStepsData.IOSChromeCameraSettings
      inSystemArrayCam.value = permissionStepsData.IosSystemCameraSteps
    } else if (DeviceUtility.isSafari) {
      inBrowserArrayMic.value = permissionStepsData.IosSafariMicrophoneSteps
      inSystemArrayMic.value = permissionStepsData.IosSystemMicrophoneSteps
      inBrowserArrayCam.value = permissionStepsData.IosSafariCameraSteps
      inSystemArrayCam.value = permissionStepsData.IosSystemCameraSteps
    }
  }
}

const checkPermission = async () => {
    const meetingConfig = useMeetingConfiguration()
    const validationResult = await meetingConfig.validateMediaAccess()

    if (validationResult.permissionRequired) {
      mediaError.value = 'Permission required'
      getImages()
    }
    else {
      mediaError.value = ''
      emits('transition')
    }
}

onMounted(async () => {
  videoPermissionReq.value =
    props.sourceType === SourceType.KIOSK || props.sourceType === SourceType.QRCODE
  await checkPermission()
})

onErrorCaptured((err) => {
  captureException(err)
})
</script>
<style scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;
div {
  font-family: 'Inter Var', 'ui-sans-serif', 'system-ui', 'sans-serif';
}
:host-context(liveswitch-window[minimized='true']) div {
  display: none !important;
}
</style>
